import React from 'react';
import { X, ArrowUpRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const UpgradeModal = ({ isOpen, onClose, feature, title, description, limitExceeded }) => {
  if (!isOpen) return null;

  const features = {
    integration: {
      title: 'Website Integration',
      description: 'Generate integration code to embed your AI chatbot',
      benefits: [
        'Custom integration code',
        'Multiple website support',
        'Real-time chat widget',
        'Seamless customer experience'
      ]
    },
    sources: {
      title: 'Knowledge Sources',
      description: 'Upload and manage your knowledge base content',
      benefits: [
        'Upload documents and files',
        'Multiple source formats',
        'Auto-sync capabilities',
        'Advanced content management'
      ]
    },
    appearance: {
      title: 'Custom Appearance',
      description: 'Customize your chatbot\'s look and feel',
      benefits: [
        'Custom colors and themes',
        'Brand logo integration',
        'Widget positioning',
        'Advanced styling options'
      ]
    },
    advanced: {
      title: 'Advanced Settings',
      description: 'Access powerful configuration options',
      benefits: [
        'Advanced AI model settings',
        'Custom conversation flows',
        'API integrations',
        'Performance analytics'
      ]
    }
  };

  const currentFeature = features[feature] || {
    title: title || 'Premium Feature',
    description: description || 'This feature requires an active subscription',
    benefits: [
      'Full platform access',
      'Premium support',
      'Advanced features',
      'Regular updates'
    ]
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#111] border border-gray-200 dark:border-white/[0.06] rounded-[20px] max-w-md w-full mx-4 relative shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>

        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center space-x-3 mb-3">
            <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center ${
              limitExceeded
                ? 'bg-orange-500'
                : 'bg-purple-600'
            }`}>
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {limitExceeded ? 'Limit Reached' : 'Upgrade required'}
            </h2>
          </div>
          <p className="text-gray-500 dark:text-gray-500 font-light text-sm leading-relaxed">
            {limitExceeded
              ? description || 'You\'ve reached your plan limit. Upgrade to continue.'
              : `${currentFeature.description} — available on paid plans.`
            }
          </p>
        </div>

        {/* Benefits */}
        <div className="px-6 pb-4">
          <div className="space-y-2">
            {currentFeature.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3 text-sm">
                <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full flex-shrink-0"></div>
                <span className="text-gray-500 dark:text-gray-500 font-light">
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-4 border-t border-gray-100 dark:border-white/[0.06] space-y-3">
          <Link
            to="/dashboard/billing"
            className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2.5 px-4 text-sm transition-colors flex items-center justify-center group"
            onClick={onClose}
          >
            View plans
            <ArrowUpRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>

          <button
            onClick={onClose}
            className="w-full rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium py-2.5 text-sm transition-colors"
          >
            {limitExceeded ? 'Maybe later' : 'Continue with free plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;